<script setup lang="ts">
import { nextTick, onScopeDispose, ref, useId, watch } from 'vue'
import CloseIcon from '@/assets/icons/close.svg?component'

/**
 * 對話框。
 *
 * 樣式與 `_auth-form.scss` 的登入卡同一套（`$color-surface` 底、`$color-border`
 * 外框、`$radius-lg`、`$shadow-lg`），所以彈窗與頁面看起來是同一個產品。
 *
 * T1 的 `Panel.vue` 只做了「蓋一層 div」：沒有焦點管理、Esc 關不掉、
 * 背景頁面照樣能捲、螢幕閱讀器會把底下的內容一起唸出來。這裡都補上：
 *
 * - **焦點鎖在對話框內**，關閉後還原到開啟前的元素
 * - **Esc 關閉**
 * - **背景鎖捲動**（用計數器，巢狀開啟也不會提早解鎖）
 * - `role="dialog"` + `aria-modal` + `aria-labelledby`
 *
 * 內容用 slot 傳入，因此個人資料、修改密碼、之後的存款取款都能共用這個外框。
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    /** 送出中時鎖住關閉，避免請求還在飛就把畫面收掉 */
    busy?: boolean
    /**
     * 疊在別的對話框上面。
     *
     * 兩個 Teleport 都掛在 body 底下，先後順序取決於誰先開 ——
     * 靠 DOM 順序決定誰在上面太脆弱，直接給一個明確的層級。
     */
    elevated?: boolean
    /**
     * 面板寬度。
     *
     * `md`（420px）是表單的寬度 —— 一欄輸入框，再寬就會讓 label 與欄位
     * 拉開到看不出是一組。`lg`（560px）給的是要**閱讀**的內容，
     * 例如公告：420px 放中文長句一行只剩十幾個字，讀起來像在讀收據。
     * `xl`（720px）是給**兩欄表單**的：欄位多到單欄會捲起來時，
     * 往橫向長比往下長好 —— 使用者一眼看得完，不必邊捲邊記剛才填了什麼。
     */
    size?: 'md' | 'lg' | 'xl'
    /**
     * 版位。
     *
     * `center` 是一般對話框。`bottom` 是**手機的底部抽屜**：貼齊畫面下緣、
     * 滿版寬、只有上緣圓角、從下方滑上來。
     *
     * 為什麼共用同一個元件而不是另寫一個：焦點鎖定、Esc 關閉、背景鎖捲動、
     * `role="dialog"` 這些東西兩者完全一樣，而它們正是最容易漏做的部分
     * （T1 的 `Panel.vue` 四項全缺）。差別只有位置與進場方向，那是 CSS 的事。
     */
    placement?: 'center' | 'bottom'
  }>(),
  { busy: false, elevated: false, size: 'md', placement: 'center' },
)

const emit = defineEmits<{ close: [] }>()

const panel = ref<HTMLElement | null>(null)
const titleId = useId()

/** 開啟前的焦點所在，關閉時還原 */
let previouslyFocused: HTMLElement | null = null

/** 可聚焦元素的選擇器。`[hidden]` 與 disabled 的要排除，否則 Tab 會停在看不見的東西上 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function focusableItems(): HTMLElement[] {
  const root = panel.value
  if (root === null) return []
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  )
}

function requestClose(): void {
  if (props.busy) return
  emit('close')
}

/**
 * 焦點循環。
 *
 * 沒有這段的話，Tab 會走出對話框跑到底下的頁面上 ——
 * 使用者看得到對話框，鍵盤焦點卻在他看不到的地方。
 */
function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.stopPropagation()
    requestClose()
    return
  }

  if (event.key !== 'Tab') return

  const items = focusableItems()
  if (items.length === 0) {
    event.preventDefault()
    return
  }

  const first = items[0]
  const last = items[items.length - 1]
  if (first === undefined || last === undefined) return

  const active = document.activeElement

  if (event.shiftKey && (active === first || !panel.value?.contains(active))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && active === last) {
    event.preventDefault()
    first.focus()
  }
}

// ── 背景捲動鎖 ────────────────────────────────────────
//
// 用模組層級的計數器而不是直接寫 `overflow: hidden`：
// 兩個對話框先後開關時，後關的那個會把鎖解掉，讓還開著的那個底下能捲。
let lockCount = 0
let previousOverflow = ''

function lockScroll(): void {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  lockCount += 1
}

function unlockScroll(): void {
  if (lockCount === 0) return
  lockCount -= 1
  if (lockCount === 0) document.body.style.overflow = previousOverflow
}

/** 這個實例目前是否持有鎖。避免重複上鎖或重複解鎖把計數器弄亂 */
let holdsLock = false

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      previouslyFocused =
        document.activeElement instanceof HTMLElement ? document.activeElement : null
      if (!holdsLock) {
        lockScroll()
        holdsLock = true
      }
      // 等 DOM 掛上去才有東西可以聚焦
      await nextTick()
      ;(focusableItems()[0] ?? panel.value)?.focus()
    } else {
      if (holdsLock) {
        unlockScroll()
        holdsLock = false
      }
      previouslyFocused?.focus()
      previouslyFocused = null
    }
  },
  { immediate: true },
)

// 元件在開啟狀態被卸載（例如路由切走）時也要解鎖，否則整頁永遠捲不動
onScopeDispose(() => {
  if (holdsLock) {
    unlockScroll()
    holdsLock = false
  }
})
</script>

<template>
  <Teleport to="body">
    <Transition name="dialog">
      <div
        v-if="open"
        class="dialog"
        :class="[`dialog--${placement}`, { 'dialog--elevated': elevated }]"
        @keydown="onKeydown"
      >
        <!-- 背景遮罩。點擊關閉，但只在遮罩本身被點到時 ——
             不能用冒泡，否則在面板裡拖曳選字後放開也會關掉 -->
        <div class="dialog__backdrop" @click.self="requestClose" />

        <div
          ref="panel"
          class="dialog__panel"
          :class="[`dialog__panel--${size}`, `dialog__panel--at-${placement}`]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          tabindex="-1"
        >
          <header class="dialog__head">
            <!-- 標題左邊的圖示，可選。純裝飾，語意由 `title` 承擔 -->
            <span v-if="$slots.icon" class="dialog__icon" aria-hidden="true">
              <slot name="icon" />
            </span>
            <h2 :id="titleId" class="dialog__title">{{ title }}</h2>
            <button
              type="button"
              class="dialog__close"
              :aria-label="$t('common.close')"
              :disabled="busy"
              @click="requestClose"
            >
              <CloseIcon class="dialog__close-icon" />
            </button>
          </header>

          <div class="dialog__body">
            <slot />
          </div>

          <footer v-if="$slots.footer" class="dialog__foot">
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.dialog {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: grid;
  padding: $space-4;

  &--center {
    place-items: center;
  }

  // 底部抽屜：貼齊下緣、滿版寬，所以連外距都不要
  &--bottom {
    align-items: end;
    justify-items: stretch;
    padding: 0;
  }

  /**
   * 手機上要把底部導覽列讓出來。
   *
   * 抽屜是被底部導覽的「热门／最新」叫出來的，兩者互斥 ——
   * 蓋住導覽列的話，想換另一榜就得先關掉再點一次。讓開之後，
   * 導覽列一直看得到、也點得到，兩顆按鈕就像分頁一樣可以直接互換。
   *
   * 遮罩也要一起讓開，否則點在導覽列上會先打到遮罩把抽屜關掉。
   */
  @media (max-width: $bp-tablet) {
    &--bottom {
      bottom: calc(#{$bottom-nav-height} + env(safe-area-inset-bottom, 0px));
    }
  }

  &--elevated {
    z-index: 110;
  }

  // 面板本身已經提亮，遮罩就不需要壓到 0.65 那麼黑 ——
  // 兩邊都很深時，眼睛只剩下一條微弱的邊界可以對焦
  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
  }

  // 尺寸與外觀比照 `.auth` 登入卡，兩者是同一個視覺語彙
  &__panel {
    // 面板色往下傳給 BaseInput / BaseButton 等共用元件
    @include panel-surface;

    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    // 內容比視窗高時讓 body 自己捲，而不是整個面板溢出畫面外。
    // `dvh`：手機的 `100vh` 是網址列收起來時的高度，用它算出來的面板
    // 在網址列展開時會有一截在畫面外 —— 而那一截通常就是送出鈕
    max-height: calc(100vh - #{$space-8});
    max-height: calc(100dvh - #{$space-8});

    &--md {
      max-width: 420px;
    }

    &--lg {
      max-width: 560px;
    }

    &--xl {
      max-width: 720px;
    }

    /**
     * 底部抽屜。
     *
     * 高度上限 58vh：**一定要留一大條看得見的背景**，
     * 使用者才知道這是蓋在頁面上的一層、點空白處就會關掉。
     * 佔滿整個畫面的話，它看起來就只是「換頁了」，人會去按返回鍵。
     * 內容比這高就在裡面捲，抽屜本身不長高。
     *
     * `max-width` 用 `!important` 蓋掉上面的 md/lg —— 抽屜一律滿版，
     * 尺寸那個 prop 在這個版位沒有意義。
     */
    &--at-bottom {
      max-width: none !important;
      max-height: 58vh;
      max-height: 58dvh;
      border-width: 1px 0 0;
      border-radius: $radius-lg $radius-lg 0 0;
      // 這裡不處理 iOS 的 home indicator —— 抽屜是坐在底部導覽列上面的，
      // 那條安全區已經由導覽列自己讓開了，再讓一次會多出一道空白
    }
    background: $color-panel;
    border: 1px solid $color-panel-border;
    border-radius: $radius-lg;
    box-shadow: $shadow-lg;

    &:focus {
      outline: none;
    }
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-3;
    padding: $space-5 $space-6 $space-3;
  }

  &__title {
    // 標題吃掉中間所有空間，關閉鈕才會貼在右緣
    flex: 1;
    margin: 0;
    font-size: $text-xl;
    font-weight: 700;
  }

  &__icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    color: $color-brand;

    :deep(svg) {
      width: 24px;
      height: 24px;
    }
  }

  &__close {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: $radius-full;
    background: none;
    color: $color-text-muted;
    cursor: pointer;
    transition:
      background $transition-fast,
      color $transition-fast;

    &:hover:not(:disabled) {
      background: $color-panel-raised;
      color: $color-text;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  &__close-icon {
    width: 18px;
    height: 18px;
  }

  &__body {
    padding: 0 $space-6 $space-6;
    overflow-y: auto;

    /**
     * 內容超出面板時的捲軸。
     *
     * 不設的話走系統預設 —— 在深色面板上是一條亮白色的粗軌，
     * 比它旁邊的內容還搶眼。公告這種長內容的對話框一定會捲到，
     * 所以這裡值得處理。
     *
     * 兩套語法都寫：`scrollbar-*` 是標準，Safari 還沒支援，
     * `::-webkit-scrollbar` 補上 WebKit。
     */
    scrollbar-width: thin;
    scrollbar-color: $color-panel-border transparent;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: $radius-full;
      background: $color-panel-border;

      &:hover {
        background: $color-text-muted;
      }
    }
  }

  &__foot {
    display: flex;
    justify-content: flex-end;
    gap: $space-3;
    padding: $space-4 $space-6;
    border-top: 1px solid $color-panel-border;
  }
}

.dialog-enter-active,
.dialog-leave-active {
  transition: opacity 0.18s ease;

  .dialog__panel {
    transition: transform 0.18s ease;
  }
}

.dialog-enter-from,
.dialog-leave-to {
  opacity: 0;

  .dialog__panel {
    transform: translateY(8px) scale(0.98);
  }

  // 抽屜是「從下面滑上來」，不是「浮現」—— 整個面板高度往下退出畫面
  .dialog__panel--at-bottom {
    transform: translateY(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dialog-enter-active,
  .dialog-leave-active {
    transition: none;

    .dialog__panel {
      transition: none;
    }
  }

  .dialog-enter-from,
  .dialog-leave-to .dialog__panel,
  .dialog-leave-to .dialog__panel--at-bottom {
    transform: none;
  }
}

@media (max-width: $bp-tablet) {
  // 只縮置中版的外距。底部抽屜本來就要貼齊三個邊，給它外距等於毀掉版位
  .dialog--center {
    padding: $space-3;
  }

  .dialog {
    &__head {
      padding: $space-4 $space-4 $space-2;
    }

    &__body {
      padding: 0 $space-4 $space-4;
    }

    &__foot {
      padding: $space-3 $space-4;
    }
  }
}
</style>
